(function (global) {
  'use strict';

  const storageKey = 'yafico:lighting:cart:v1';
  let sequence = 0;

  function failure(code, message, cause) {
    const error = new Error(message);
    error.code = code;
    if (cause) error.cause = cause;
    return error;
  }

  function cents(value) {
    return Number.isSafeInteger(value) && value >= 0;
  }

  function variantId(value) {
    const identifier = String(value == null ? '' : value);
    if (!/^[1-9]\d*$/.test(identifier)) {
      throw failure('INVALID_VARIANT', 'A valid Shopify variant is required.');
    }
    return identifier;
  }

  class YaficoCart {
    constructor(config = {}) {
      this.mode = config.mode === 'shopify' ? 'shopify' : 'preview';
      this.enabled = config.enabled === true;
      this.currency = /^[A-Z]{3}$/.test(config.currency || '') ? config.currency : 'EUR';
      this.kit = Object.assign({}, config.kit);
      this.root = typeof config.root === 'string' ? config.root : '/';
      if (!/^\/(?!\/)[A-Za-z0-9/_-]*$/.test(this.root)) this.root = '/';
      if (!this.root.endsWith('/')) this.root += '/';
      this.queue = Promise.resolve();
      this.memoryCart = this.emptyCart();
      try {
        this.storage = global.localStorage;
      } catch (error) {
        this.storage = null;
      }
      if (this.mode === 'preview') {
        this.storageListener = (event) => {
          if (event.key !== storageKey && event.key !== null) return;
          this.enqueue(() => this.emit(this.readPreview())).catch(() => {});
        };
        global.addEventListener('storage', this.storageListener);
      }
    }

    emptyCart() {
      return { items: [], item_count: 0, total_price: 0, currency: this.currency };
    }

    enqueue(operation) {
      const pending = this.queue.then(operation);
      this.queue = pending.catch(() => {});
      return pending;
    }

    emit(cart) {
      global.dispatchEvent(new global.CustomEvent('yafico:cart', { detail: cart }));
      return cart;
    }

    read() {
      return this.enqueue(() => this.mode === 'shopify' ? this.readShopify() : this.readPreview());
    }

    add(product, quantity = 1) {
      return this.enqueue(async () => {
        if (this.mode === 'shopify' && !this.enabled) {
          throw failure('CART_DISABLED', 'Ordering is not enabled yet.');
        }
        this.validateProduct(product, quantity);
        if (this.mode === 'shopify') return this.addShopify(product, quantity);
        return this.previewLock(() => {
          const cart = this.readPreview();
          const shadeId = product.variantId || 'preview-' + product.id;
          const lightId = this.kit.variantId || 'preview-led-kit';
          const existing = this.existingPair(cart, shadeId, lightId);
          if (existing && existing.quantity + quantity <= 20) {
            return this.emit(this.savePreview(this.previewCart(this.previewQuantity(cart, existing.pairId, existing.quantity + quantity))));
          }
          const pairId = this.newPairId(cart);
          const additions = [
            this.previewItem(pairId, 'shade', shadeId, product.name + ' — Lampenschirm', product.shadePrice, quantity, product.colorKey ? { variant_title: product.colorLabel, sku: product.sku, image: product.studioImage, colorKey: product.colorKey } : {}),
            this.previewItem(pairId, 'light', lightId, 'Bambu Lab LED Kit 001', this.kit.price, quantity)
          ];
          return this.emit(this.savePreview(this.previewCart(cart.items.concat(additions))));
        });
      });
    }

    remove(pairId) {
      return this.enqueue(async () => {
        if (typeof pairId !== 'string' || !pairId) {
          throw failure('INVALID_PAIR', 'A cart group is required.');
        }
        if (this.mode === 'shopify') {
          try {
            return this.emit(await this.rollbackPair(pairId));
          } catch (error) {
            if (error.cart) this.emit(error.cart);
            throw error;
          }
        }
        return this.previewLock(() => {
          const items = this.readPreview().items.filter((item) => item.properties._yafico_pair !== pairId);
          return this.emit(this.savePreview(this.previewCart(items)));
        });
      });
    }

    setQuantity(pairId, quantity) {
      return this.enqueue(async () => {
        if (typeof pairId !== 'string' || !pairId) {
          throw failure('INVALID_PAIR', 'A cart group is required.');
        }
        if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 20) {
          throw failure('INVALID_QUANTITY', 'Quantity must be between 1 and 20.');
        }
        if (this.mode === 'preview') {
          return this.previewLock(() => {
            const cart = this.readPreview();
            return this.emit(this.savePreview(this.previewCart(this.previewQuantity(cart, pairId, quantity))));
          });
        }
        return this.changeShopifyQuantity(pairId, quantity, await this.readShopify());
      });
    }

    previewQuantity(cart, pairId, quantity) {
      return cart.items.map((item) => item.properties._yafico_pair === pairId
        ? this.previewItem(pairId, item.properties._yafico_role, item.variant_id, item.product_title, item.price, quantity, { ...item, colorKey: item.properties._yafico_color })
        : item);
    }

    existingPair(cart, shadeId, lightId) {
      const groups = new Map();
      for (const item of cart.items) {
        const pairId = item.properties && item.properties._yafico_pair;
        if (typeof pairId !== 'string' || !pairId) continue;
        if (!groups.has(pairId)) groups.set(pairId, []);
        groups.get(pairId).push(item);
      }
      for (const [pairId, items] of groups) {
        if (items.length !== 2) continue;
        const shade = items.find((item) => item.properties._yafico_role === 'shade');
        const light = items.find((item) => item.properties._yafico_role === 'light');
        if (!shade || !light || shade.quantity !== light.quantity) continue;
        if (String(shade.variant_id) !== String(shadeId) || String(light.variant_id) !== String(lightId)) continue;
        return { pairId, quantity: shade.quantity };
      }
      return null;
    }

    async changeShopifyQuantity(pairId, quantity, before) {
        const selection = this.pairItems(before, pairId);
        if (!selection.length) return this.emit(before);
        let requestError;
        for (const item of selection) {
          try {
            await this.request('cart/change.js', { id: item.key, quantity });
          } catch (error) {
            if (!requestError) requestError = error;
          }
        }
        let cart = await this.readShopify();
        const updated = this.pairItems(cart, pairId);
        if (!requestError && updated.length === selection.length && updated.every((item) => item.quantity === quantity)) {
          return this.emit(cart);
        }
        const fallback = updated.length ? Math.min(...updated.map((item) => item.quantity)) : 0;
        for (const item of updated) {
          if (item.quantity !== fallback) {
            try {
              await this.request('cart/change.js', { id: item.key, quantity: fallback });
            } catch (error) {}
          }
        }
        try {
          cart = await this.readShopify();
        } catch (error) {}
        this.emit(cart);
        const error = failure('CART_QUANTITY_FAILED', 'The quantity could not be updated. Please check your cart and try again.', requestError);
        error.cart = cart;
        throw error;
    }

    removeLine(lineKey) {
      return this.enqueue(async () => {
        if (typeof lineKey !== 'string' || !lineKey) {
          throw failure('INVALID_LINE', 'A cart line key is required.');
        }
        const before = this.mode === 'shopify' ? await this.readShopify() : this.readPreview();
        const target = before.items.find((item) => item.key === lineKey);
        if (!target) return this.emit(before);
        const pairId = target.properties?._yafico_pair;
        const selection = pairId ? this.pairItems(before, pairId) : [];
        if (selection.some(item => item.properties._yafico_role === 'shade') && selection.some(item => item.properties._yafico_role === 'light')) {
          throw failure('SELECTION_REQUIRED', 'Remove the selected shade and LED kit together.');
        }
        if (this.mode === 'preview') {
          return this.emit(this.savePreview(this.previewCart(before.items.filter((item) => item.key !== lineKey))));
        }
        let requestError;
        let cart;
        try {
          await this.request('cart/change.js', { id: lineKey, quantity: 0 });
        } catch (error) {
          requestError = error;
        }
        try {
          cart = await this.readShopify();
          this.emit(cart);
          if (!cart.items.some((item) => item.key === lineKey)) return cart;
        } catch (error) {
          if (!requestError) requestError = error;
        }
        const error = failure('CART_REMOVE_FAILED', 'The item could not be removed. Please check your cart and try again.', requestError);
        if (cart) error.cart = cart;
        throw error;
      });
    }

    validateProduct(product, quantity) {
      if (!Number.isSafeInteger(quantity) || quantity < 1) {
        throw failure('INVALID_QUANTITY', 'Quantity must be a positive whole number.');
      }
      if (!product || typeof product.name !== 'string' || !product.name.trim()) {
        throw failure('INVALID_PRODUCT', 'A valid product is required.');
      }
      if (!cents(product.price) || !cents(product.shadePrice) || !cents(this.kit.price)
        || product.price !== product.shadePrice + this.kit.price
        || !Number.isSafeInteger(product.price * quantity)) {
        throw failure('INVALID_PRICE', 'The component prices must equal the displayed total.');
      }
      if (this.mode === 'shopify') {
        if (product.available !== true || this.kit.available !== true) {
          throw failure('UNAVAILABLE', 'One of the selected components is unavailable.');
        }
        variantId(product.variantId);
        variantId(this.kit.variantId);
        if (String(product.variantId) === String(this.kit.variantId)) {
          throw failure('INVALID_VARIANT', 'Shade and light source must be separate variants.');
        }
      }
    }

    newPairId(cart) {
      let pairId;
      do {
        sequence += 1;
        const random = global.crypto && typeof global.crypto.randomUUID === 'function'
          ? global.crypto.randomUUID()
          : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
        pairId = 'yl-' + random + '-' + sequence;
      } while (this.pairItems(cart, pairId).length);
      return pairId;
    }

    pairItems(cart, pairId) {
      return cart.items.filter((item) => item.properties && item.properties._yafico_pair === pairId);
    }

    async request(endpoint, body) {
      const options = {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      };
      if (body) {
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(body);
      }
      const response = await global.fetch(this.root + endpoint, options);
      let result;
      try {
        result = await response.json();
      } catch (error) {
        throw failure('INVALID_RESPONSE', 'The cart returned an unreadable response.', error);
      }
      if (!response.ok) {
        const message = result && (result.description || result.message);
        throw failure('CART_REQUEST_FAILED', typeof message === 'string' ? message : 'The cart request failed.');
      }
      return result;
    }

    async readShopify() {
      const cart = await this.request('cart.js');
      if (!cart || !Array.isArray(cart.items) || !cents(cart.total_price)
        || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0
        || cart.items.some((item) => !item || !Number.isSafeInteger(item.quantity) || item.quantity < 1
          || typeof item.key !== 'string' || !item.key)) {
        throw failure('INVALID_RESPONSE', 'The cart returned an invalid response.');
      }
      return cart;
    }

    completePair(cart, pairId, shadeId, lightId, quantity) {
      const items = this.pairItems(cart, pairId);
      let shades = 0;
      let lights = 0;
      for (const item of items) {
        if (item.properties._yafico_role === 'shade' && String(item.variant_id) === shadeId) {
          shades += item.quantity;
        } else if (item.properties._yafico_role === 'light' && String(item.variant_id) === lightId) {
          lights += item.quantity;
        } else {
          return false;
        }
      }
      return shades === quantity && lights === quantity;
    }

    async addShopify(product, quantity) {
      const before = await this.readShopify();
      const shadeId = variantId(product.variantId);
      const lightId = variantId(this.kit.variantId);
      const existing = this.existingPair(before, shadeId, lightId);
      if (existing && existing.quantity + quantity <= 20) {
        return this.changeShopifyQuantity(existing.pairId, existing.quantity + quantity, before);
      }
      const pairId = this.newPairId(before);
      let addError;
      let cart;
      try {
        await this.request('cart/add.js', {
          items: [
            { id: shadeId, quantity, properties: { _yafico_pair: pairId, _yafico_role: 'shade', ...(['bone-white', 'natural-amber'].includes(product.colorKey) ? { _yafico_color: product.colorKey } : {}) } },
            { id: lightId, quantity, properties: { _yafico_pair: pairId, _yafico_role: 'light' } }
          ]
        });
      } catch (error) {
        addError = error;
      }
      try {
        cart = await this.readShopify();
      } catch (error) {
        if (!addError) addError = error;
      }
      if (!addError && cart && this.completePair(cart, pairId, shadeId, lightId, quantity)) {
        return this.emit(cart);
      }
      try {
        cart = await this.rollbackPair(pairId, cart);
        this.emit(cart);
      } catch (error) {
        const rollbackError = failure('CART_ROLLBACK_FAILED', 'The addition could not be verified. Check your cart before trying again.', error);
        if (error.cart || cart) {
          rollbackError.cart = error.cart || cart;
          this.emit(rollbackError.cart);
        }
        throw rollbackError;
      }
      const error = failure('CART_ADD_FAILED', 'The selected components could not both be added. Please try again.', addError);
      error.cart = cart;
      throw error;
    }

    async rollbackPair(pairId, knownCart) {
      let cart = knownCart;
      try {
        cart = cart || await this.readShopify();
        let attempts = 0;
        while (this.pairItems(cart, pairId).length) {
          attempts += 1;
          if (attempts > 30) throw failure('CART_ROLLBACK_FAILED', 'The cart group could not be removed.');
          const item = this.pairItems(cart, pairId)[0];
          await this.request('cart/change.js', { id: item.key, quantity: 0 });
          cart = await this.readShopify();
        }
        return cart;
      } catch (error) {
        try {
          cart = await this.readShopify();
        } catch (readError) {}
        const rollbackError = failure('CART_ROLLBACK_FAILED', 'The cart group could not be removed. Check your cart before trying again.', error);
        if (cart) rollbackError.cart = cart;
        throw rollbackError;
      }
    }

    previewItem(pairId, role, identifier, title, price, quantity, metadata = {}) {
      return {
        key: pairId + ':' + role,
        id: identifier,
        variant_id: identifier,
        quantity,
        price,
        final_line_price: price * quantity,
        product_title: title,
        properties: { _yafico_pair: pairId, _yafico_role: role, ...(['bone-white', 'natural-amber'].includes(metadata.colorKey) ? { _yafico_color: metadata.colorKey } : {}) },
        ...(typeof metadata.variant_title === 'string' && metadata.variant_title.length <= 160 ? { variant_title: metadata.variant_title } : {}),
        ...(typeof metadata.sku === 'string' && metadata.sku.length <= 100 ? { sku: metadata.sku } : {}),
        ...(typeof metadata.image === 'string' && metadata.image.length <= 2048 ? { image: metadata.image } : {})
      };
    }

    previewCart(items) {
      return {
        items,
        item_count: items.reduce((total, item) => total + item.quantity, 0),
        total_price: items.reduce((total, item) => total + item.final_line_price, 0),
        currency: this.currency
      };
    }

    readPreview() {
      let stored = this.memoryCart;
      try {
        if (this.storage) {
          const raw = this.storage.getItem(storageKey);
          stored = raw && raw.length < 1000000 ? JSON.parse(raw) : this.emptyCart();
        }
      } catch (error) {
        if (error instanceof SyntaxError || error.name === 'SyntaxError') stored = this.emptyCart();
        else this.storage = null;
      }
      if (!stored || !Array.isArray(stored.items) || stored.items.length > 1000) return this.emptyCart();
      const groups = new Map();
      const items = [];
      for (const item of stored.items) {
        const properties = item && item.properties;
        if (!properties || typeof properties._yafico_pair !== 'string' || !properties._yafico_pair
          || !['shade', 'light'].includes(properties._yafico_role)
          || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || !cents(item.price)
          || !Number.isSafeInteger(item.price * item.quantity) || typeof item.product_title !== 'string'
          || !['string', 'number'].includes(typeof item.variant_id)) return this.emptyCart();
        const pairId = properties._yafico_pair;
        const role = properties._yafico_role;
        const group = groups.get(pairId) || {};
        if (group[role] || (group.quantity && group.quantity !== item.quantity)) return this.emptyCart();
        group[role] = true;
        group.quantity = item.quantity;
        groups.set(pairId, group);
        items.push(this.previewItem(pairId, role, item.variant_id, item.product_title, item.price, item.quantity, { ...item, colorKey: properties._yafico_color }));
      }
      const cart = this.previewCart(items);
      if (!cents(cart.total_price) || !Number.isSafeInteger(cart.item_count)) return this.emptyCart();
      this.memoryCart = cart;
      return cart;
    }

    savePreview(cart) {
      if (!cents(cart.total_price) || !Number.isSafeInteger(cart.item_count) || cart.items.length > 1000) {
        throw failure('CART_LIMIT', 'The cart limit has been reached.');
      }
      this.memoryCart = cart;
      if (this.storage) {
        try {
          this.storage.setItem(storageKey, JSON.stringify(cart));
        } catch (error) {
          this.storage = null;
        }
      }
      return cart;
    }

    previewLock(operation) {
      const locks = global.navigator && global.navigator.locks;
      return locks && typeof locks.request === 'function'
        ? locks.request(storageKey, operation)
        : operation();
    }
  }

  global.YaficoCart = YaficoCart;
})(window);
